/**
 * Cartoon-cross sparkle starfield.
 *
 * Stars are drawn 4-point cross sparkles painted into a Canvas2D
 * sprite atlas, then scattered as a Points cloud across a large shell
 * around the camera. Cross-shape reads as illustrated rather than
 * photoreal — fits the cel-shading language of the rest of the scene.
 *
 * Sits *inside* the cel-banded background sphere so the dome's discrete
 * bands provide the deep-space wash and stars sparkle on top.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  Points,
  PointsMaterial,
  type Object3D,
  type Texture,
} from "three";

const STAR_COUNT = 500;
const SHELL_RADIUS = 180;
const SPRITE_SIZE = 64;

function buildSparkleSprite(): Texture | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = SPRITE_SIZE;
  canvas.height = SPRITE_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);
  const c = SPRITE_SIZE / 2;
  // Soft white core glow.
  const glow = ctx.createRadialGradient(c, c, 0, c, c, SPRITE_SIZE * 0.42);
  glow.addColorStop(0, "rgba(255,255,255,1)");
  glow.addColorStop(0.35, "rgba(255,255,255,0.45)");
  glow.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(c, c, SPRITE_SIZE * 0.42, 0, Math.PI * 2);
  ctx.fill();
  // 4-point cross sparkle — the cartoon star cue.
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.lineCap = "round";
  ctx.lineWidth = SPRITE_SIZE * 0.06;
  ctx.beginPath();
  ctx.moveTo(c, SPRITE_SIZE * 0.05);
  ctx.lineTo(c, SPRITE_SIZE * 0.95);
  ctx.moveTo(SPRITE_SIZE * 0.05, c);
  ctx.lineTo(SPRITE_SIZE * 0.95, c);
  ctx.stroke();
  // Diagonal mini-cross — subtle.
  ctx.strokeStyle = "rgba(255,255,255,0.45)";
  ctx.lineWidth = SPRITE_SIZE * 0.03;
  ctx.beginPath();
  ctx.moveTo(SPRITE_SIZE * 0.25, SPRITE_SIZE * 0.25);
  ctx.lineTo(SPRITE_SIZE * 0.75, SPRITE_SIZE * 0.75);
  ctx.moveTo(SPRITE_SIZE * 0.75, SPRITE_SIZE * 0.25);
  ctx.lineTo(SPRITE_SIZE * 0.25, SPRITE_SIZE * 0.75);
  ctx.stroke();
  return new CanvasTexture(canvas);
}

export interface Starfield {
  object: Object3D;
  tick(dt: number): void;
  detach(): void;
}

export function createStarfield(): Starfield {
  const positions = new Float32Array(STAR_COUNT * 3);
  const colors = new Float32Array(STAR_COUNT * 3);
  const sizes = new Float32Array(STAR_COUNT);

  for (let i = 0; i < STAR_COUNT; i += 1) {
    // Uniform on sphere via spherical-coordinate sampling.
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    const r = SHELL_RADIUS * (0.85 + Math.random() * 0.15);
    const offset = i * 3;
    positions[offset] = r * Math.sin(phi) * Math.cos(theta);
    positions[offset + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[offset + 2] = r * Math.cos(phi);

    // Star color — mostly white with occasional warm/cool tint.
    const tint = Math.random();
    let c: Color;
    if (tint < 0.15) c = new Color(1.0, 0.84, 0.62);
    else if (tint < 0.30) c = new Color(0.78, 0.90, 1.0);
    else c = new Color(1.0, 1.0, 1.0);
    const brightness = 0.7 + Math.random() * 0.3;
    colors[offset] = c.r * brightness;
    colors[offset + 1] = c.g * brightness;
    colors[offset + 2] = c.b * brightness;

    sizes[i] = 0.6 + Math.random() * 1.6;
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  geometry.setAttribute("color", new BufferAttribute(colors, 3));

  const sparkle = buildSparkleSprite();
  const material = new PointsMaterial({
    map: sparkle ?? undefined,
    size: 2.0,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.95,
    vertexColors: true,
    blending: AdditiveBlending,
    depthWrite: false,
  });

  const points = new Points(geometry, material);
  points.name = "starfield";

  const tick = (dt: number): void => {
    points.rotation.y += dt * 0.005;
  };

  const detach = (): void => {
    geometry.dispose();
    material.dispose();
    if (sparkle) sparkle.dispose();
    points.removeFromParent();
  };

  return { object: points, tick, detach };
}

export const __testing = { STAR_COUNT, SHELL_RADIUS, SPRITE_SIZE };
